import AddIcon from '@mui/icons-material/Add';
import { Alert, Box } from '@mui/material';
import { Button, SearchBar } from '@peritasai/ui-components';
import type { LocationEntity } from '@peritasai/repository';
import { useLocations } from '@peritasai/repository';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { GridPaginationModel } from '@mui/x-data-grid-premium';
import { LocationDrawer } from './LocationDrawer';
import { DrawerMode } from '../../utils/drawer';
import { LocationsTable } from './LocationsTable';

export const LocationsPage = () => {
  const { t } = useTranslation(['periverse']);
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 10,
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(DrawerMode.Create);
  const [selectedLocation, setSelectedLocation] = useState<LocationEntity | null>(null);

  const { data, isLoading, isError } = useLocations({
    search: activeSearch || undefined,
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  });

  const rows = data?.data ?? [];
  const rowCount = data?.total ?? 0;

  const handleInputChange = (value: string) => {
    setSearchInput(value);
    if (!value) {
      setActiveSearch('');
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleSearch = (value: string) => {
    setActiveSearch(value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClear = () => {
    setSearchInput('');
    setActiveSearch('');
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleRowClick = (row: LocationEntity) => {
    setSelectedLocation(row);
    setDrawerMode(DrawerMode.Edit);
    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setSelectedLocation(null);
    setDrawerMode(DrawerMode.Create);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
        <SearchBar
          value={searchInput}
          onChange={handleInputChange}
          onSearch={handleSearch}
          onClear={handleClear}
          placeholder={`${t('pages.entityManagement.searchPlaceholder', { entityName: t('pages.entityManagement.items.locations') })}...`}
          width={{ xs: '100%', sm: '45%' }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{ flexShrink: 0 }}
        >
          {t('common.add')}
        </Button>
      </Box>

      {isError && (
        <Alert severity="error">
          {t('pages.entityManagement.loadError')}
        </Alert>
      )}

      <LocationsTable
        rows={rows}
        rowCount={rowCount}
        isLoading={isLoading}
        hasSearch={activeSearch.length > 0}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          setPaginationModel(model);
        }}
        onRowClick={handleRowClick}
      />

      <LocationDrawer
        open={drawerOpen}
        mode={drawerMode}
        selectedLocation={selectedLocation}
        onClose={handleDrawerClose}
      />
    </Box>
  );
};
